// ─────────────────────────────────────────────────────────────────────
// «PROVE YOU ARE STILL THERE» — the transport's side of it (#750 M2b-1).
//
// The API refuses organization authority on a session whose last
// authentication is more than twelve hours old: 403 with
// `reauthentication_required` and the window in seconds. Something has to turn
// that into a prompt, and the transport cannot call the thing that owns the
// prompt — that module imports `apiFetch` to run the ceremony, so a direct
// call either way is a cycle.
//
// A LEAF, for exactly the reason `impersonationToken.ts` is one. Same shape,
// deliberately: a registered handler, a report function the transport calls,
// and no state of its own beyond the handler.
//
// ── WHAT THIS IS NOT ───────────────────────────────────────────────
//
// It is not a session ending. The person is who they were a second ago, their
// session is live, and signing them out would be the wrong answer to «you
// authenticated a while back» — they would have to do more, not less, to get
// back to where they were. `onUnauthorized` handles the case where identity
// really is gone.
//
// It is not an authorization answer either. A 403 without this code is an
// ordinary refusal — a capability the account does not hold — and a prompt
// would be a lie about what went wrong.
// ─────────────────────────────────────────────────────────────────────

/** The API's code. Not translated, ever — see CLAUDE.md on error CODES. */
export const REAUTHENTICATION_REQUIRED = 'reauthentication_required';

export interface StaleAuthentication {
  /** How long the window is, per the server, or null if it did not say. */
  windowSeconds: number | null;
}

let onStale: ((info: StaleAuthentication) => void) | null = null;

/** Registered once, by whatever owns the prompt. */
export function setReauthenticationRequiredHandler(
  fn: ((info: StaleAuthentication) => void) | null,
): void {
  onStale = fn;
}

/**
 * Called by the transport when the API says the authentication is too old.
 *
 * A no-op with no handler registered, which is the state the app is in until
 * the prompt is mounted — the refusal still reaches the caller as a 403, so
 * nothing is swallowed; it simply is not offered a way to fix it yet.
 */
export function reportReauthenticationRequired(windowSeconds: number | null): void {
  onStale?.({ windowSeconds });
}

// ── AND THE OTHER DIRECTION: «PROVEN» (#1141) ──────────────────────
//
// Something that stopped because the authentication was too old — a
// background poll, a screen that loaded nothing — needs to know when the
// person has proved themselves again, or it stays stopped until a reload. The
// prompt reports it; any number of listeners hear it.
const onFresh = new Set<() => void>();

/** Listen for a completed step-up. Returns the unsubscribe. */
export function onReauthenticated(fn: () => void): () => void {
  onFresh.add(fn);
  return () => {
    onFresh.delete(fn);
  };
}

/** Called by the prompt once the step-up succeeded. */
export function reportReauthenticated(): void {
  for (const fn of [...onFresh]) fn();
}
